import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { AppBundle } from '../utils/app-bundles';
import { useLocation } from 'react-router-dom';
import { useActiveBundle } from './active-bundle';

const MAX_HISTORY_LENGTH = 10;

export interface NavigationContextHistoryState {
  url: string;
  app: AppBundle | null;
}

export interface INavigationContext {
  history: NavigationContextHistoryState[];
}

export const NavigationContext = createContext<INavigationContext | null>(null);

export function useNavigationContext() {
  const context = useContext(NavigationContext);
  if (context == null) {
    throw new Error('useNavigationContext must be used within a NavigationContext');
  }
  return context;
}

export function NavigationContextProvider({ children }: { children: ReactNode }) {
  const [history, setHistory] = useState<NavigationContextHistoryState[]>([]);
  const location = useLocation();

  const activeApp = useActiveBundle();
  const activeAppRef = useRef(activeApp);
  useEffect(() => {
    activeAppRef.current = activeApp;
  }, [activeApp]);

  useEffect(() => {
    setHistory((prevHistory) => {
      const lastHistoryItem = prevHistory[prevHistory.length - 1];
      const newHistoryItem = {
        url: `${location.pathname}${location.search}`,
        app: activeAppRef.current ?? null,
      };

      if (lastHistoryItem?.url === newHistoryItem.url) {
        return prevHistory;
      }

      const newHistory = [...prevHistory, newHistoryItem];

      if (newHistory.length > MAX_HISTORY_LENGTH) {
        newHistory.shift();
      }

      return newHistory;
    });
  }, [location]);

  return <NavigationContext.Provider value={{ history }}>{children}</NavigationContext.Provider>;
}
